import React from 'react';

/**
 * React Error Boundary — catches unhandled render errors in any child component
 * and shows a friendly fallback UI instead of a blank screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console in dev; swap for a real error-reporting service (Sentry etc.) in prod
    if (process.env.NODE_ENV !== 'production') {
      console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (fallback) return fallback;

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', padding: '2rem', textAlign: 'center',
      }}>
        <div style={{
          background: 'var(--surface, #fff)', border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 12, padding: '2.5rem', maxWidth: 480, width: '100%',
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: 'var(--text-primary, #111)', marginBottom: '0.75rem', fontWeight: 700 }}>
            Something went wrong
          </h2>
          <p style={{ color: 'var(--text-muted, #6b7280)', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            An unexpected error occurred in this section. Your data is safe.
          </p>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '0.75rem', fontSize: '0.75rem', textAlign: 'left', overflowX: 'auto',
              marginBottom: '1.5rem', color: '#dc2626',
            }}>
              {this.state.error.message}
            </pre>
          )}
          <button
            className="btn btn-primary"
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginRight: '0.75rem' }}
          >
            Try again
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
